import React, { useEffect, useState } from "react";
import NextLink from "next/link";
import IconLeft from "$/icons/icon-left.svg";
import { MenuItem } from "@/components/Menu/menu.interface";
import { getMenuURL, menuListData } from "@/components/Menu/helpers";
import Image from "@/components/Image";
import routes from "@/helpers/router";
import { RefsObjectType } from "@/components/PageHome/interfaces";
import styles from "./MenuWeb.module.scss";

interface PillarMenuProps {
  item: MenuItem;
}

const PillarMenu: React.FC<PillarMenuProps> = ({ item }) => {
  return (
    <NextLink prefetch={false} href={routes.pillar(item?.slug)} className={styles.header}>
      <div className={styles.leftSection}>
        {item?.iconURL && (
          <div className={styles.navIcon}>
            <Image src={item?.iconURL} alt="icon" height={32} width={32} />
          </div>
        )}
        {item?.label}
      </div>
      <IconLeft className={`${styles.arrows}`} width={20} height={20} />
    </NextLink>
  );
};

interface ClusterMenuProps {
  item: MenuItem;
}

const ClusterMenu: React.FC<ClusterMenuProps> = ({ item }) => {
  return (
    <div className={styles.col}>
      <NextLink
        prefetch={false}
        href={getMenuURL(item.type, item.slug)}
        className={`${styles.titleText} ${styles.head}`}
      >
        {item.label}
      </NextLink>
      {item.children?.map((childItem) => (
        <NextLink
          key={childItem?.slug}
          href={getMenuURL(childItem.type, childItem.slug)}
          className={`${styles.titleText} `}
          prefetch={false}
        >
          {childItem.label}
        </NextLink>
      ))}
    </div>
  );
};

interface MenuWebProps {
  className?: string;
  trimesterImageUrl?: string;
  menuSlug?: string;
  activeSlug?: string;
  navRef?: RefsObjectType;
}

const MenuWeb: React.FC<MenuWebProps> = (props) => {
  const { className = "", menuSlug, activeSlug, navRef } = props;

  const [offsetFromMiddle, setOffsetFromMiddle] = useState<number>(0);

  useEffect(() => {
    const element = navRef?.[menuSlug];

    if (element) {
      const rect = element.getBoundingClientRect();
      const viewportMiddle = window.innerWidth / 2;
      const gapOffset = 20;
      const itemRootWidth = 319;
      setOffsetFromMiddle(viewportMiddle - rect.left + gapOffset - itemRootWidth / 2);
    }
  }, []);

  const selectedMenuList: MenuItem = menuListData.find((item) => item.slug === menuSlug);

  if (!selectedMenuList) return null;

  const itemsWithChildren = selectedMenuList?.children?.filter((item) => !!item.children?.length);
  const itemsWithoutChildren = selectedMenuList?.children?.filter((item) => !item.children?.length);
  const splitInto = itemsWithoutChildren.length > 5 ? Math.ceil(itemsWithoutChildren.length / 2) : 5;
  const itemsWithoutChildrenContainer = itemsWithoutChildren?.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / splitInto);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, [] as MenuItem[][]);

  const totalColumns = itemsWithChildren.length + itemsWithoutChildrenContainer.length;

  const widthClass = ["four", "one", "two", "three"][totalColumns % 4];

  const customStyles = totalColumns === 1 ? { left: `calc(50% - ${offsetFromMiddle}px)` } : undefined;

  return (
    <div
      style={customStyles}
      className={`${styles.DDRoot} ${className} ${styles[widthClass]} ${activeSlug === menuSlug ? styles.open : ""}`}
    >
      {itemsWithChildren?.map((item) => (
        <div key={item?.slug} className={styles.itemRoot}>
          <PillarMenu item={item} />
          {item?.children && (
            <div className={styles.subTitleRoot}>
              {item?.children.map((childItem) => <ClusterMenu item={childItem} key={childItem?.slug} />)}
            </div>
          )}
        </div>
      ))}
      {itemsWithoutChildrenContainer.map((childItems: MenuItem[], index) => (
        <div className={styles.itemRoot} key={`container_${index}`}>
          {childItems?.map((item) => <PillarMenu item={item} key={item?.slug} />)}
        </div>
      ))}
    </div>
  );
};

export default MenuWeb;
